import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

export type TipoItem = 'texto' | 'link';

export interface ItemResumo {
  _id: string;
  titulo: string;
  slug: string;
  resumo: string;
  categoria: string;
  tipo: TipoItem;
  url?: string;
  tags: string[];
  destaque: boolean;
  atualizadoEm: string;
}

const CATEGORIAS = ['Marca', 'Fotos', 'Acervo', 'Modelos', 'Formação', 'Normas', 'Avisos'];

const ICONES: Record<string, string> = {
  Marca: 'pi pi-palette',
  Fotos: 'pi pi-images',
  Acervo: 'pi pi-folder-open',
  Modelos: 'pi pi-copy',
  Formação: 'pi pi-graduation-cap',
  Normas: 'pi pi-verified',
  Avisos: 'pi pi-megaphone',
};

const Biblioteca: React.FC = () => {
  const { theme } = useTheme();
  const { isAdmin } = useAuth();
  const isDark = theme === 'dark';

  const [itens, setItens] = useState<ItemResumo[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<ItemResumo[]>('/items')
      .then((res) => !cancelled && setItens(res.data))
      .catch(() => !cancelled && setErro(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return itens.filter((item) => {
      if (categoria && item.categoria !== categoria) return false;
      if (!termo) return true;
      return (
        item.titulo.toLowerCase().includes(termo) ||
        item.resumo.toLowerCase().includes(termo) ||
        item.tags.some((t) => t.toLowerCase().includes(termo))
      );
    });
  }, [itens, busca, categoria]);

  const comItens = CATEGORIAS.filter((c) => itens.some((i) => i.categoria === c));

  const cardClasses = `group flex gap-4 rounded-2xl border p-4 transition-all duration-300 hover:-translate-y-0.5 ${
    isDark
      ? 'border-white/10 bg-white/[0.03] hover:border-primary/40 hover:bg-white/[0.06]'
      : 'border-gray-200 bg-white hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10'
  }`;

  const Miolo: React.FC<{ item: ItemResumo }> = ({ item }) => (
    <>
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent">
        <i className={`${ICONES[item.categoria] || 'pi pi-file'} text-lg text-white`}></i>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.titulo}</h3>
          {/* O selo diz de cara se abre aqui ou sai do site, evitando o clique surpresa. */}
          {item.tipo === 'link' && (
            <span className="flex items-center gap-1 rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-medium text-sky-400">
              <i className="pi pi-external-link text-[10px]"></i>
              link externo
            </span>
          )}
          {item.destaque && (
            <span className="rounded-full bg-primary/20 px-2 py-0.5 text-xs font-medium text-primary-light">
              destaque
            </span>
          )}
        </div>
        {item.resumo && (
          <p className={`mt-1 text-sm leading-relaxed ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
            {item.resumo}
          </p>
        )}
        <p className={`mt-2 text-xs ${isDark ? 'text-white/35' : 'text-gray-400'}`}>
          {item.categoria} · atualizado em {new Date(item.atualizadoEm).toLocaleDateString('pt-BR')}
        </p>
      </div>
    </>
  );

  return (
    <div className="flex-1 px-4 py-8">
      <div className="glass-card mx-auto w-full max-w-3xl">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h2 className="bg-gradient-to-r from-primary-light to-accent bg-clip-text text-2xl font-bold text-transparent">
              Biblioteca
            </h2>
            <p className={`mt-1 text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
              {loading ? 'Carregando...' : `${visiveis.length} ite${visiveis.length !== 1 ? 'ns' : 'm'}`}
            </p>
          </div>
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-r from-primary to-accent">
            <i className="pi pi-folder-open text-2xl text-white"></i>
          </div>
        </div>

        {isAdmin && (
          <Link to="/biblioteca/novo" className="btn-gradient mb-5 flex items-center justify-center gap-2">
            <i className="pi pi-plus text-sm"></i>
            Adicionar item
          </Link>
        )}

        <div className="relative mb-4">
          <i className={`pi pi-search absolute left-4 top-1/2 -translate-y-1/2 text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}></i>
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome, resumo ou tag..."
            className="input-dark !pl-11"
          />
        </div>

        {comItens.length > 1 && (
          <div className="mb-5 flex flex-wrap gap-2">
            {[null, ...comItens].map((c) => (
              <button
                key={c ?? 'todos'}
                onClick={() => setCategoria(c)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  categoria === c
                    ? 'bg-primary text-white'
                    : isDark
                      ? 'bg-white/5 text-white/60 hover:text-white'
                      : 'bg-gray-100 text-gray-600 hover:text-primary'
                }`}
              >
                {c ?? 'Todos'}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center py-12">
            <i className="pi pi-spin pi-spinner text-4xl text-primary-light"></i>
          </div>
        ) : erro ? (
          <p className="py-12 text-center text-red-400">Não foi possível carregar a biblioteca.</p>
        ) : visiveis.length === 0 ? (
          <div className="flex flex-col items-center py-12">
            <div className={`mb-4 flex h-20 w-20 items-center justify-center rounded-full ${isDark ? 'bg-surface-light/50' : 'bg-gray-200'}`}>
              <i className={`pi pi-folder-open text-4xl ${isDark ? 'text-white/40' : 'text-gray-400'}`}></i>
            </div>
            <p className={`text-center ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {busca.trim() ? `Nada encontrado com "${busca.trim()}".` : 'A biblioteca ainda está vazia.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {visiveis.map((item) =>
              // Link externo abre em aba nova. Texto abre a tela de leitura aqui dentro.
              item.tipo === 'link' && item.url ? (
                <a key={item._id} href={item.url} target="_blank" rel="noopener noreferrer" className={cardClasses}>
                  <Miolo item={item} />
                </a>
              ) : (
                <Link key={item._id} to={`/biblioteca/${item.slug}`} className={cardClasses}>
                  <Miolo item={item} />
                </Link>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Biblioteca;
